import { memo } from 'react';
import { Flex, Box, Switch } from '@chakra-ui/react';
import { LuPlus, LuGripVertical, LuPencil } from 'react-icons/lu';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import type { DragEndEvent } from '@dnd-kit/core';
import type { MenuContentCategory, MenuContentBadgeDef } from './types';
import { EDIT_HOVER, EDIT_TRANSITION } from './types';
import type { MenuTheme } from '../../api/menus';
import SortableRow from './SortableRow';
import MenuItem from './MenuItem';
import InlineEdit from './InlineEdit';

interface CategorySectionProps {
  category: MenuContentCategory;
  theme: MenuTheme;
  hoverBg: string;
  currency: string;
  isEditMode: boolean;
  badgeDefinitions?: MenuContentBadgeDef[];
  soldOutLabel: string;
  onCategoryClick?: (id: string) => void;
  onItemClick?: (id: string) => void;
  onAddItem?: (categoryId: string) => void;
  onReorderItems?: (categoryId: string, event: DragEndEvent) => void;
  onImageClick?: (url: string) => void;
  onQuickEditItem?: (itemId: string, updates: { name?: string; description?: string; price?: number; is_visible?: boolean; is_sold_out?: boolean }) => void;
  onQuickEditItemImage?: (itemId: string) => void;
  onQuickEditCategory?: (categoryId: string, updates: { name?: string; is_visible?: boolean }) => void;
}

const CategorySection = memo(({
  category,
  theme: mt,
  hoverBg,
  currency,
  isEditMode,
  badgeDefinitions,
  soldOutLabel,
  onCategoryClick,
  onItemClick,
  onAddItem,
  onReorderItems,
  onImageClick,
  onQuickEditItem,
  onQuickEditItemImage,
  onQuickEditCategory,
}: CategorySectionProps) => {
  const { t } = useTranslation();
  const isQuickEditMode = !!onQuickEditItem;

  return (
    <Box mb="24px">
      {/* Category heading */}
      {isEditMode && onCategoryClick ? (
        <Flex
          alignItems="center"
          gap="8px"
          mb="12px"
          pb="8px"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="transparent"
          cursor="pointer"
          borderRadius="6px"
          mx="-8px"
          px="8px"
          transition={EDIT_TRANSITION}
          _hover={EDIT_HOVER}
          onClick={() => onCategoryClick(category.id)}
          css={{ '&:hover .edit-icon': { opacity: 1 } }}
        >
          <Box fontSize="18px" fontWeight="600" color={mt.text_color} flex="1">
            {category.name}
          </Box>
          <Box className="edit-icon" opacity={0} transition="opacity 0.15s" color={mt.secondary_text_color} flexShrink={0}>
            <LuPencil size={14} />
          </Box>
        </Flex>
      ) : isQuickEditMode && onQuickEditCategory ? (
        <Flex alignItems="center" gap="8px" mb="12px" pb="8px" borderBottomWidth="1px" borderColor={mt.border_color}>
          <Box flex="1">
            <InlineEdit
              value={category.name}
              onSave={(name) => onQuickEditCategory(category.id, { name })}
              fontSize="18px"
              fontWeight="600"
              color={mt.text_color}
            />
          </Box>
          <Flex alignItems="center" gap="6px" flexShrink={0}>
            <Switch.Root size="sm" checked={category.is_visible !== false} onCheckedChange={(e) => onQuickEditCategory(category.id, { is_visible: e.checked })}>
              <Switch.HiddenInput />
              <Switch.Control><Switch.Thumb /></Switch.Control>
            </Switch.Root>
            <Box fontSize="11px" fontWeight="500" color="#999999">{t('menus.visible', 'Visible')}</Box>
          </Flex>
        </Flex>
      ) : (
        <Box fontSize="18px" fontWeight="600" color={mt.text_color} mb="12px" pb="8px" borderBottomWidth="1px" borderColor={mt.border_color}>
          {category.name}
        </Box>
      )}

      {/* Items list */}
      {isEditMode && onReorderItems ? (
        <DndContext collisionDetection={closestCenter} onDragEnd={(e) => onReorderItems(category.id, e)}>
          <SortableContext items={category.items.map(i => i.id)} strategy={verticalListSortingStrategy}>
            <Flex direction="column" gap="16px">
              {category.items.map((item) => (
                <SortableRow key={item.id} id={item.id}>
                  {({ dragRef, dragStyle, dragHandleProps }) => (
                    <Flex ref={dragRef} style={dragStyle} gap="12px" alignItems="center">
                      <Flex {...dragHandleProps} alignItems="center" style={{ cursor: 'grab' }} onClick={(e) => e.stopPropagation()}>
                        <LuGripVertical style={{ width: 14, height: 14, color: mt.secondary_text_color }} />
                      </Flex>
                      <Box flex="1">
                        <MenuItem
                          item={item}
                          theme={mt}
                          currency={currency}
                          isEditMode={isEditMode}
                          badgeDefinitions={badgeDefinitions}
                          soldOutLabel={soldOutLabel}
                          onItemClick={onItemClick}
                          onImageClick={onImageClick}
                        />
                      </Box>
                    </Flex>
                  )}
                </SortableRow>
              ))}
              {onAddItem && (
                <Flex
                  alignItems="center"
                  gap="6px"
                  cursor="pointer"
                  px="10px"
                  py="6px"
                  borderRadius="6px"
                  borderWidth="1px"
                  borderStyle="dashed"
                  borderColor={mt.border_color}
                  color={mt.secondary_text_color}
                  _hover={{ backgroundColor: hoverBg, borderColor: mt.secondary_text_color }}
                  onClick={() => onAddItem(category.id)}
                >
                  <LuPlus style={{ width: 14, height: 14 }} />
                  <Box fontSize="13px">{t('menus.addItem')}</Box>
                </Flex>
              )}
            </Flex>
          </SortableContext>
        </DndContext>
      ) : (
        <Flex direction="column">
          {category.items.map((item) => (
            <MenuItem
              key={item.id}
              item={item}
              theme={mt}
              currency={currency}
              isEditMode={isEditMode}
              badgeDefinitions={badgeDefinitions}
              soldOutLabel={soldOutLabel}
              onItemClick={onItemClick}
              onImageClick={onImageClick}
              onQuickEdit={onQuickEditItem}
              onQuickEditImage={onQuickEditItemImage}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
});

export default CategorySection;
